/* PreNumbers Financial Builder — Companies (reskinned).
   REPLACES: src/components/FinancialBuilder/CompaniesPage/CompaniesPage.jsx

   Logic (add / save / delete / select, industry & currency cascades) is
   unchanged from your original — only the markup is reskinned. CompanyDetails
   is still exported (the Dashboard renders it read-only). */

import { useState, useContext } from 'react';

import { FinancialBuilderContext } from '../FinancialBuilderPage.jsx';
import { companyRenderFields, industryDetails } from '../../constants.jsx';
import { genericPOSTDatabaseCall, genericDELETEDatabaseCall } from '../../helperFunctions.jsx';
import { renderFbField, PageHead, StatTile, FbIcons } from '../fbUI.jsx';
import { ApartmentOutlined, AppstoreOutlined, CheckOutlined, DollarOutlined, DownOutlined } from '@ant-design/icons';

const { ArrowRightOutlined, PlusOutlined, DeleteOutlined, SaveOutlined } = FbIcons;

async function addCompanyToDatabase(company) { return genericPOSTDatabaseCall('/api/fb/addCompany', { company }, 'company'); }
async function saveCompanyToDatabase(company) { return genericPOSTDatabaseCall('/api/fb/saveCompanyInfo', { company }, 'company info'); }
async function deleteCompanyFromDatabase(companyId) { return genericDELETEDatabaseCall('/api/fb/deleteCompany', { companyId }, 'company'); }

/* Renders the company's fields (+ actions when editable). No card chrome — the
   caller supplies it (a stack card here, a ShowAndHide panel in the Dashboard). */
export function CompanyDetails({ company, setCompanies, messageApi, editable = true, setCompanySelectedID, spotRates }) {
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const saveCompany = async (c) => {
    setSaving(true); messageApi.loading('Saving company info...', 0);
    try { await saveCompanyToDatabase(c); messageApi.destroy(); messageApi.success('Company info saved successfully'); }
    catch (e) { console.error('Error saving company info:', e); messageApi.destroy(); messageApi.error('Failed to save company info'); }
    setSaving(false);
  };
  const deleteCompany = async (companyId) => {
    setDeleting(true); messageApi.loading('Deleting company...', 0);
    try {
      await deleteCompanyFromDatabase(companyId);
      setCompanies((prev) => prev.filter((c) => c.id !== companyId));
      setCompanySelectedID(""); messageApi.destroy(); messageApi.success('Company deleted successfully');
    } catch (e) { console.error('Error deleting company:', e); messageApi.destroy(); messageApi.error('Failed to delete company'); }
    setDeleting(false);
  };

  const handleInputChange = (event, changedCompany, key) => {
    const newValue = event.target.value;
    const updated = { ...changedCompany, [key]: newValue };
    if (key === 'industry') {
      const keys = Object.keys(industryDetails[newValue]);
      let newProducts = [...updated.products];
      for (let i = 0; i < newProducts.length; i++) {
        newProducts[i] = { ...newProducts[i], growth_rate: industryDetails[newValue]['default_growth_rate'], growth_adjustment: industryDetails[newValue]['default_growth_adjustment'] };
      }
      updated.products = newProducts;
      for (const k of keys) updated[k] = industryDetails[newValue][k];
    }
    if (key === 'in_currency' || key === 'out_currency') {
      const conversionKey = updated.in_currency + '_' + updated.out_currency;
      updated.spot_rate = (spotRates && spotRates[conversionKey]) || '1.000';
    }
    setCompanies((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
  };

  const handleInputBlur = (event, changedCompany, key, field) => {
    let newValue = event.target.value;
    if (field.regexCondition && field.errorMessage && !field.regexCondition.test(newValue)) { messageApi.error(field.errorMessage); newValue = field.default || ''; }
    if (newValue === '') { messageApi.error('This field cannot be empty'); newValue = field.default || ''; }
    setCompanies((prev) => prev.map((c) => (c.id === changedCompany.id ? { ...c, [key]: newValue } : c)));
  };

  return (
    <>
      <div className="pn-fields">
        {companyRenderFields.map((f) => renderFbField(f, company, handleInputChange, editable, handleInputBlur))}
        <div className="pn-field">
          <label>Spot Rate</label>
          <div className="pn-input-wrap"><input className="pn-control" value={company.spot_rate ?? ''} readOnly /></div>
        </div>
      </div>
      {editable && (
        <div className="pn-editor-foot">
          <button type="button" className="pn-btn pn-btn-danger" disabled={deleting} onClick={() => deleteCompany(company.id)}><DeleteOutlined /> Delete company</button>
          <div className="sp" />
          <button type="button" className="pn-btn pn-btn-accent" disabled={saving} onClick={() => saveCompany(company)}><SaveOutlined /> Save company</button>
        </div>
      )}
    </>
  );
}

function CompanyCard({ company, isActive, onSelect, setCompanies, messageApi, spotRates, setCompanySelectedID }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={"pn-stack-card" + (open ? " open" : "")}>
      <div className="pn-stack-head" onClick={() => setOpen((o) => !o)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <span className="pn-radio" data-on={isActive ? "1" : "0"} title="Set as active model"
            onClick={(e) => { e.stopPropagation(); onSelect(company.id); }}>{isActive && <i />}</span>
          <div style={{ minWidth: 0 }}>
            <div className="pn-stack-name">{company.name || 'Untitled company'}</div>
            <div className="pn-stack-meta"><span>{company.industry}</span><span>·</span><span>{company.products?.length || 0} products</span><span>·</span><span>{company.out_currency}</span></div>
          </div>
        </div>
        <span className="pn-badge" style={isActive ? { background: 'var(--pn-accent-dim)', color: 'var(--pn-accent)' } : { background: 'var(--pn-tint-08)', color: 'var(--pn-fg-tertiary)' }}>{isActive ? 'Active model' : 'Select'}</span>
        <span className="chev"><DownOutlined style={{ fontSize: 14 }} /></span>
      </div>
      {open && (
        <div className="pn-stack-body">
          <CompanyDetails company={company} editable setCompanies={setCompanies} messageApi={messageApi} spotRates={spotRates} setCompanySelectedID={setCompanySelectedID} />
        </div>
      )}
    </div>
  );
}

function CompaniesPage({ contextToUse }) {
  const { companies, setCompanies, goTo, messageApi, companySelectedID, setCompanySelectedID, spotRates } = useContext(contextToUse || FinancialBuilderContext);
  const [newCompanyName, setNewCompanyName] = useState('');

  const totalProducts = companies.reduce((n, c) => n + (c.products?.length || 0), 0);
  const active = companies.find((c) => String(c.id) === String(companySelectedID));

  const addCompany = async () => {
    if (!newCompanyName) { messageApi.error('Please enter a company name'); return; }
    messageApi.loading('Adding company...', 0);
    try {
      const newCompany = await addCompanyToDatabase(newCompanyName);
      const keys = Object.keys(industryDetails[newCompany.industry]);
      let newProducts = [...newCompany.products];
      for (let i = 0; i < newProducts.length; i++) {
        newProducts[i] = { ...newProducts[i], growth_rate: industryDetails[newCompany.industry]['default_growth_rate'], growth_adjustment: industryDetails[newCompany.industry]['default_growth_adjustment'] };
      }
      newCompany.products = newProducts;
      for (const k of keys) newCompany[k] = industryDetails[newCompany.industry][k];
      setCompanies((prev) => [...prev, newCompany]);
      setNewCompanyName('');
      messageApi.destroy(); messageApi.success('Company added successfully');
    } catch (e) { console.error('Error adding company:', e); messageApi.destroy(); messageApi.error('Failed to add company'); }
  };

  return (
    <div className="pn-wrap">
      <PageHead eyebrow="Workspace" title="Companies"
        sub={<>Manage your modeling entities. Pick one as the <b>active model</b> to build products and projections.</>}>
        <button type="button" className="pn-btn pn-btn-cta" disabled={!companySelectedID} onClick={() => goTo(2)}>
          {companySelectedID ? "Continue to Products" : "Select a Company"} {companySelectedID && <ArrowRightOutlined />}
        </button>
      </PageHead>

      <div className="pn-stats">
        <StatTile icon={<ApartmentOutlined />} k="Companies" v={companies.length} />
        <StatTile icon={<AppstoreOutlined />} k="Products" v={totalProducts} />
        <StatTile icon={<CheckOutlined />} k="Active model" v={active ? active.name : '—'} />
        <StatTile icon={<DollarOutlined />} k="Currency" v={active ? active.out_currency : '—'} />
      </div>

      <div className="pn-stack">
        {companies.map((c) => (
          <CompanyCard key={c.id} company={c} isActive={String(c.id) === String(companySelectedID)}
            onSelect={setCompanySelectedID} setCompanies={setCompanies} messageApi={messageApi}
            spotRates={spotRates} setCompanySelectedID={setCompanySelectedID} />
        ))}
      </div>

      <div className="pn-addrow">
        <div className="pn-input-wrap" style={{ flex: 1 }}>
          <input className="pn-control" placeholder="New company name" value={newCompanyName}
            onChange={(e) => setNewCompanyName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addCompany()} />
        </div>
        <button type="button" className="pn-btn pn-btn-ghost" onClick={addCompany}><PlusOutlined /> Add company</button>
      </div>
    </div>
  );
}

export default CompaniesPage;
